import React, { useMemo } from "react";
import { Dropdown } from "primereact/dropdown";
import { MultiSelect } from "primereact/multiselect";
import { GOVERNI, MEMBRI } from "../../data/governi";

const MEMBRI_FLAT = MEMBRI.flat();

const FOTO_BASE = "https://documenti.camera.it/_dati/costituente/costituenti/foto";

const selectedChipStyle = {
    display: "inline-flex",
    alignItems: "center",
    width: "fit-content",
    maxWidth: "100%",
    padding: "0.25rem 0.55rem",
    borderRadius: "16px",
    background: "#e5e7eb",
    color: "#334155",
    verticalAlign: "middle",
};

const selectedChipIconStyle = {
    cursor: "pointer",
    flexShrink: 0,
    fontSize: "0.85rem",
};

const selectedChipTextStyle = {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    minWidth: 0,
    whiteSpace: "normal",
    overflowWrap: "anywhere",
    lineHeight: 1.2,
};

const selectedChipRoleStyle = {
    fontSize: "0.78rem",
    color: "#64748b",
};

const membroOptionStyle = {
    display: "flex",
    alignItems: "center",
    gap: "0.5rem",
    width: "100%",
    minWidth: 0,
    whiteSpace: "normal",
};

const membroOptionTextStyle = {
    minWidth: 0,
    whiteSpace: "normal",
    overflowWrap: "anywhere",
    lineHeight: 1.35,
};

const fotoUrl = (idpersona) => {
    const numericId = parseInt(String(idpersona ?? "").slice(2), 10);
    return Number.isNaN(numericId) ? null : `${FOTO_BASE}/d${numericId}.jpg`;
};

const Foto = ({ m, size = 42 }) => {
    const src = fotoUrl(m?.idpersona);

    if (!src) return null;

    return (
        <img
            src={src}
            alt={`${m.cognome} ${m.nome}`}
            loading="lazy"
            width={size}
            height={size}
            style={{ borderRadius: "50%", objectFit: "cover", flexShrink: 0 }}
            onError={(e) => { e.currentTarget.style.visibility = "hidden"; }}
        />
    );
};

export default function FiltroMembroGoverno({ value, onChange }) {
    const { governo, membro } = value;
    const selectedMembri = Array.isArray(membro) ? membro : [];

    const membriOptions = useMemo(() => {
        if (!governo) return [];
        return MEMBRI_FLAT
            .filter((m) => m.governo === governo)
            .map((m) => ({
                ...m,
                label: `${m.cognome} ${m.nome}`.trim(),
                value: m.id,
            }))
            .sort((a, b) =>
                a.cognome.localeCompare(b.cognome, "it") ||
                a.nome.localeCompare(b.nome, "it")
            );
    }, [governo]);

    const membriById = useMemo(
        () => new Map(membriOptions.map((m) => [m.id, m])),
        [membriOptions]
    );

    const onGovernoChange = (e) => {
        onChange({ governo: e.value ?? null, membro: [] });
    };

    const removeSelectedMembro = (id, event) => {
        event.preventDefault();
        event.stopPropagation();
        onChange({ ...value, membro: selectedMembri.filter((selectedId) => selectedId !== id) });
    };

    const membroItemTemplate = (option) => (
        <div style={membroOptionStyle}>
            <Foto m={option} />
            <div style={membroOptionTextStyle}>
                <span className="font-medium">{option.cognome} {option.nome}</span>
                {" — "}
                <em style={{ fontSize: "0.85em" }}>{option.ruolo}</em>
            </div>
        </div>
    );

    const membroSelectedItemTemplate = (id) => {
        const membroSelezionato = membriById.get(id);

        if (!membroSelezionato) return id;

        return (
            <span className="inline-flex align-items-center gap-2 mr-2" style={selectedChipStyle}>
                <Foto m={membroSelezionato} size={24} />
                <span style={selectedChipTextStyle}>
                    <span>{membroSelezionato.cognome} {membroSelezionato.nome}</span>
                    <span style={selectedChipRoleStyle}>{membroSelezionato.ruolo}</span>
                </span>
                <i
                    className="pi pi-times-circle"
                    role="button"
                    tabIndex={0}
                    aria-label={`Rimuovi ${membroSelezionato.cognome} ${membroSelezionato.nome}`}
                    style={selectedChipIconStyle}
                    onClick={(event) => removeSelectedMembro(id, event)}
                    onKeyDown={(event) => {
                        if (event.key === "Enter" || event.key === " ") {
                            removeSelectedMembro(id, event);
                        }
                    }}
                />
            </span>
        );
    };

    return (
        <>
            <div className="mb-3">
                <label className="font-semibold">Governi della Costituente</label>
                <Dropdown
                    value={governo}
                    options={GOVERNI}
                    optionLabel="label"
                    optionValue="id"
                    onChange={onGovernoChange}
                    placeholder="Seleziona un Governo"
                    showClear
                    className="w-full"
                />
            </div>

            <div>
                <label className="font-semibold">Seleziona membro di Governo</label>
                <MultiSelect
                    value={selectedMembri}
                    options={membriOptions}
                    onChange={(e) => onChange({ ...value, membro: e.value ?? [] })}
                    disabled={!governo}
                    placeholder={governo ? "Seleziona uno o più membri..." : "Seleziona prima un Governo"}
                    showClear
                    filter
                    filterBy="cognome,nome,ruolo"
                    filterPlaceholder="Cerca membro..."
                    display="chip"
                    optionValue="id"
                    selectedItemTemplate={membroSelectedItemTemplate}
                    maxSelectedLabels={3}
                    className="w-full filter-chip-multiselect"
                    itemTemplate={membroItemTemplate}
                    pt={{
                        item: {
                            className: 'pl-3 pt-1 pb-1',
                            style: { alignItems: "center", whiteSpace: "normal" },
                        },
                    }}
                />
                {governo && (
                    <div style={{ marginTop: 4, fontSize: "0.8rem", fontStyle: "italic", color: "#666" }}>
                        (sono presentati solo i nominativi che hanno effettuato interventi)
                    </div>
                )}
            </div>
        </>
    );
}
